import type { z } from "zod";
import { DataBundleSchema, type DataBundle } from "./bundle";
import {
  ConfigSchema,
  CurrencyRowSchema,
  EntityKindRowSchema,
  FeatureFlagRowSchema,
  RewardRowSchema,
  StringRowSchema,
  type GameConfig,
} from "./schemas";

export type EntityKindDef = z.infer<typeof EntityKindRowSchema>;
export type CurrencyDef = z.infer<typeof CurrencyRowSchema>;
export type RewardDef = z.infer<typeof RewardRowSchema>;
export type FeatureFlagDef = z.infer<typeof FeatureFlagRowSchema>;
export type StringDef = z.infer<typeof StringRowSchema>;

/** Fully typed, parsed game data. Built once at startup from the bundle. */
export interface GameData {
  version: string;
  builtAt: string;
  config: GameConfig;
  entityKinds: ReadonlyMap<string, EntityKindDef>;
  currencies: ReadonlyMap<string, CurrencyDef>;
  rewards: ReadonlyMap<string, RewardDef>;
  featureFlags: ReadonlyMap<string, FeatureFlagDef>;
  strings: ReadonlyMap<string, StringDef>;
}

function table<T extends object>(
  bundle: DataBundle,
  name: string,
  schema: z.ZodType<T, unknown>,
  key: keyof T & string,
): Map<string, T> {
  const rows = bundle.tables[name];
  if (!rows) throw new Error(`data bundle is missing table "${name}"`);
  const out = new Map<string, T>();
  for (const row of rows) {
    const parsed = schema.parse(row);
    out.set(String(parsed[key]), parsed);
  }
  return out;
}

/**
 * Parse a bundle into typed GameData. Throws loudly on any schema mismatch: bad data must never
 * be coerced into a half-working game.
 */
export function loadGameData(rawBundle: unknown): GameData {
  const bundle = DataBundleSchema.parse(rawBundle);
  const configRows = bundle.tables.config ?? [];
  const configRecord: Record<string, string> = {};
  for (const row of configRows) configRecord[row.key ?? ""] = row.value ?? "";
  const config = ConfigSchema.parse(configRecord);

  return {
    version: bundle.hash,
    builtAt: bundle.builtAt,
    config,
    entityKinds: table(bundle, "entity_kinds", EntityKindRowSchema, "id"),
    currencies: table(bundle, "currencies", CurrencyRowSchema, "id"),
    rewards: table(bundle, "rewards", RewardRowSchema, "id"),
    featureFlags: table(bundle, "feature_flags", FeatureFlagRowSchema, "key"),
    strings: table(bundle, "strings", StringRowSchema, "key"),
  };
}

/**
 * Localised string lookup. Today only `en` exists; the resolver pattern means adding a locale is
 * a change here and in strings.csv, never at call sites. Missing keys return the key itself so
 * they are visible on screen rather than silently blank.
 */
export function resolveString(data: GameData, key: string, _locale = "en"): string {
  return data.strings.get(key)?.en ?? key;
}

/** Default flag values from data; the client layers server config + debug overrides on top. */
export function defaultFeatureFlags(data: GameData): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [key, def] of data.featureFlags) out[key] = def.default;
  return out;
}
